import type {
  InvestmentAccountReturns,
  InvestmentCurrencyReturns,
  InvestmentPeriodReturn,
  InvestmentReturnsBreakdown,
  InvestmentReturnsIssue,
} from 'shared';

import { formatCurrency } from '../../lib/format/currency';

const PERIOD_LABELS: Record<InvestmentPeriodReturn['period'], string> = {
  '1M': '1 month',
  '3M': '3 months',
  YTD: 'Year to date',
  '1Y': '1 year',
  SINCE_TRACKING: 'Since tracking began',
};

const CATEGORY_LABELS: Record<InvestmentAccountReturns['categories'][number]['category'], string> =
  {
    CONTRIBUTION: 'Contributions',
    WITHDRAWAL: 'Withdrawals',
    DIVIDEND: 'Dividends (reinvested)',
    INCOME_TRANSFER: 'Income account transfers',
    INTEREST: 'Interest',
    FEE: 'Fees',
    CORPORATE_ACTION: 'Corporate action proceeds',
    OTHER_INCOME: 'Other income',
    UNCLASSIFIED: 'Unclassified',
  };

function signedMoney(value: string | null, currency: string): string {
  if (value === null) return 'Unavailable';
  const numeric = Number(value);
  const formatted = formatCurrency(value, currency);
  return numeric > 0 ? `+${formatted}` : formatted;
}

function signedPct(value: string | null): string {
  if (value === null) return 'Unavailable';
  const numeric = Number(value);
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(1)}%`;
}

function StatusLabel({ status }: { status: InvestmentCurrencyReturns['status'] }) {
  const styles = {
    COMPLETE: 'border-success/50 text-success',
    PARTIAL: 'border-warn/50 text-warn',
    UNAVAILABLE: 'border-border text-text-muted',
  } as const;
  const labels = {
    COMPLETE: 'Complete history',
    PARTIAL: 'Partial: see reconciliation',
    UNAVAILABLE: 'Unavailable',
  } as const;
  return (
    <span className={`rounded border px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function Issues({
  issues,
  currency,
}: {
  issues: readonly InvestmentReturnsIssue[];
  currency: string;
}) {
  if (issues.length === 0) return null;
  return (
    <ul className="space-y-2 text-sm" aria-label="Reconciliation issues">
      {issues.map((issue, index) => (
        <li key={`${issue.kind}-${index}`} className="rounded border border-warn/40 p-3">
          <p className="text-warn">{issue.message}</p>
          {issue.amount !== null ? (
            <p className="mt-1 tabular-nums text-text-muted">
              Amount: {formatCurrency(issue.amount, currency)}
            </p>
          ) : null}
          {issue.items.length > 0 ? (
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-text-muted">
              {issue.items.map((item) => (
                <li key={item} className="break-words">
                  {item}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function Breakdown({
  breakdown,
  currency,
}: {
  breakdown: InvestmentReturnsBreakdown;
  currency: string;
}) {
  const rows: Array<[string, string]> = [
    ['Contributions', signedMoney(breakdown.contributions, currency)],
    ['Withdrawals', signedMoney(breakdown.withdrawals, currency)],
    ...(breakdown.openingCash !== null
      ? ([['Unexplained opening cash', signedMoney(breakdown.openingCash, currency)]] as Array<
          [string, string]
        >)
      : []),
    [
      `Closed positions (${breakdown.closedPositionsCount})`,
      signedMoney(breakdown.closedPositionsRealised, currency),
    ],
    ['Dividends (reinvested)', signedMoney(breakdown.dividends, currency)],
    ['Income account transfers', signedMoney(breakdown.incomeTransfers, currency)],
    ['Interest', signedMoney(breakdown.interest, currency)],
    ['Fees', signedMoney(breakdown.fees, currency)],
    ['Corporate action proceeds', signedMoney(breakdown.corporateActions, currency)],
    ['Other income', signedMoney(breakdown.otherIncome, currency)],
  ];
  return (
    <div>
      <dl className="grid gap-x-6 gap-y-2 text-sm tabular-nums sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 border-b border-border py-1">
            <dt className="text-text-muted">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {breakdown.closedPositionsExcludedCount > 0 ? (
        <p className="mt-2 text-xs text-text-muted">
          {breakdown.closedPositionsExcludedCount} closed positions are excluded from the
          closed-position total because their trades don&apos;t reconcile (usually corporate
          actions). Their cash is still included in the account gain.
        </p>
      ) : null}
    </div>
  );
}

function Periods({ periods }: { periods: readonly InvestmentPeriodReturn[] }) {
  const reasons = new Set(periods.map((period) => period.unavailableReason));
  const sharedReason = periods.every((period) => period.returnPct === null) && reasons.size === 1;
  if (sharedReason) {
    return (
      <p className="rounded border border-border bg-bg p-3 text-sm text-text-muted">
        {periods[0]?.unavailableReason}
      </p>
    );
  }
  return (
    <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5" aria-label="Period returns">
      {periods.map((period) => (
        <li key={period.period} className="min-w-0 rounded border border-border bg-bg p-3">
          <div className="text-sm text-text-muted">{PERIOD_LABELS[period.period]}</div>
          {period.returnPct !== null ? (
            <>
              <div className="mt-1 text-lg font-semibold tabular-nums">
                {signedPct(period.returnPct)}
              </div>
              <div className="text-xs text-text-muted">
                {period.startDate} to {period.endDate}
              </div>
            </>
          ) : (
            <p className="mt-1 text-xs text-text-muted">{period.unavailableReason}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function Headline({
  returns,
  currency,
}: {
  returns: InvestmentCurrencyReturns | InvestmentAccountReturns;
  currency: string;
}) {
  const items: Array<[string, string, string | null]> = [
    ['Current value', formatCurrency(returns.currentValue, currency), null],
    ['Net contributions', formatCurrency(returns.netContributions, currency), null],
    [
      'Gain since history began',
      signedMoney(returns.gain, currency),
      returns.gainPct === null ? null : `${signedPct(returns.gainPct)} on net contributions`,
    ],
    [
      'Annualised return',
      signedPct(returns.annualisedReturnPct),
      'Money-weighted (accounts for when you paid in)',
    ],
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {items.map(([label, value, note]) => (
        <div key={label} className="min-w-0 rounded border border-border bg-bg p-4">
          <dt className="text-sm text-text-muted">{label}</dt>
          <dd className="mt-2 break-words text-xl font-semibold tabular-nums">{value}</dd>
          {note ? <dd className="mt-1 text-xs text-text-muted">{note}</dd> : null}
        </div>
      ))}
    </dl>
  );
}

function AccountDetails({ account }: { account: InvestmentAccountReturns }) {
  return (
    <details className="rounded border border-border p-3">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 font-medium">
        <span>{account.accountName}</span>
        <StatusLabel status={account.status} />
        {account.annualisedReturnPct !== null ? (
          <span className="text-sm tabular-nums text-text-muted">
            {signedPct(account.annualisedReturnPct)} a year ·{' '}
            {signedMoney(account.gain, account.currency)}
          </span>
        ) : null}
      </summary>
      <div className="mt-4 space-y-4">
        {account.historyStart ? (
          <p className="text-sm text-text-muted">
            Measured from {account.historyStart} (start of imported HL history) to{' '}
            {account.valuationDate}.
          </p>
        ) : null}
        <Headline returns={account} currency={account.currency} />
        <Issues issues={account.issues} currency={account.currency} />
        <Breakdown breakdown={account.breakdown} currency={account.currency} />
        <Periods periods={account.periods} />
        {account.categories.length > 0 ? (
          <section aria-label={`${account.accountName} cash transaction classification`}>
            <h5 className="mb-2 text-sm font-medium">How HL cash transactions were classified</h5>
            <ul className="divide-y divide-border text-sm">
              {account.categories.map((category) => (
                <li key={category.category} className="py-2">
                  <div className="flex flex-wrap justify-between gap-2 tabular-nums">
                    <span>
                      {CATEGORY_LABELS[category.category]} ({category.count})
                    </span>
                    <span>{formatCurrency(category.total, account.currency)}</span>
                  </div>
                  <p className="mt-0.5 break-words text-xs text-text-muted">
                    e.g. {category.examples.join('; ')}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </details>
  );
}

export function InvestmentReturnsPanel({
  returns,
  accounts,
}: {
  returns: InvestmentCurrencyReturns;
  accounts: readonly InvestmentAccountReturns[];
}) {
  return (
    <section aria-label="Returns" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">Returns</h3>
        <StatusLabel status={returns.status} />
      </div>
      {returns.status === 'UNAVAILABLE' ? (
        <p className="text-sm text-text-muted">
          Import HL transaction history for these accounts to calculate since-inception returns.
        </p>
      ) : (
        <>
          <p className="text-sm text-text-muted">
            From {returns.historyStart} to {returns.valuationDate}. Gain is current value minus
            everything you paid in, less withdrawals, so it includes realised gains, dividends,
            interest and fees.
          </p>
          <Headline returns={returns} currency={returns.currency} />
          {returns.status === 'PARTIAL' ? (
            <p className="text-sm text-warn" role="status">
              Some history couldn&apos;t be reconciled. Open each account below to see what&apos;s
              missing and how it affects these figures.
            </p>
          ) : null}
          <Issues
            issues={returns.issues.filter((issue) => issue.kind === 'ACCOUNT_EXCLUDED')}
            currency={returns.currency}
          />
          <Breakdown breakdown={returns.breakdown} currency={returns.currency} />
        </>
      )}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Period returns</h4>
        <p className="text-xs text-text-muted">
          Time-weighted between holdings snapshots, adjusted for contributions and withdrawals.
          Import an HL holdings snapshot regularly (for example monthly) to build this history.
        </p>
        <Periods periods={returns.periods} />
      </div>
      <div className="space-y-2">
        <h4 className="text-sm font-medium">By account</h4>
        {accounts.map((account) => (
          <AccountDetails key={account.accountId} account={account} />
        ))}
      </div>
    </section>
  );
}
